import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, Filter, Search, Download, Eye, Edit, Trash2, 
  Clock, Calendar, User, Building2, DollarSign, Package,
  CheckCircle2, AlertTriangle, XCircle, ArrowRight, 
  ChevronDown, ChevronUp, FileText, Users, Settings,
  TrendingUp, TrendingDown, BarChart2, RefreshCw,
  Activity, Info, Workflow
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { useNavigate } from 'react-router-dom';

const PurchaseRequestsTab = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [expandedRequest, setExpandedRequest] = useState(null);
  const [selectedRequests, setSelectedRequests] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState({ type: '', requestId: '', requestTitle: '' });
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [requests, setRequests] = useState([
    {
      id: 'PR-2024-001',
      title: 'IT Equipment for New Office',
      requester: 'Ahmed Hassan',
      department: 'IT Department',
      priority: 'high',
      status: 'pending',
      category: 'Technology',
      totalAmount: 45000,
      currency: 'SAR',
      items: [
        { name: 'Laptops', quantity: 15, unitPrice: 2500, total: 37500 },
        { name: 'Monitors', quantity: 15, unitPrice: 500, total: 7500 }
      ],
      submittedDate: '2024-03-15',
      requiredDate: '2024-03-25',
      description: 'New laptops and monitors for the expanding IT team',
      attachments: ['specifications.pdf', 'budget_approval.docx'],
      workflow: [
        { step: 'Submitted', user: 'Ahmed Hassan', date: '2024-03-15 09:30', status: 'completed' },
        { step: 'Department Review', user: 'Sarah IT Manager', date: '2024-03-15 14:20', status: 'completed' },
        { step: 'Budget Approval', user: 'Finance Team', date: '2024-03-16 11:15', status: 'completed' },
        { step: 'Procurement Review', user: 'Procurement Team', date: '2024-03-17 10:45', status: 'current' },
        { step: 'Vendor Selection', user: '', date: '', status: 'pending' },
        { step: 'PO Creation', user: '', date: '', status: 'pending' },
        { step: 'Approved', user: '', date: '', status: 'pending' }
      ]
    },
    {
      id: 'PR-2024-002',
      title: 'Kitchen Equipment Replacement',
      requester: 'Fatima Al-Zahra',
      department: 'Operations',
      priority: 'medium',
      status: 'approved',
      category: 'Equipment',
      totalAmount: 125000,
      currency: 'SAR',
      items: [
        { name: 'Commercial Ovens', quantity: 2, unitPrice: 45000, total: 90000 },
        { name: 'Refrigeration Units', quantity: 3, unitPrice: 12000, total: 36000 }
      ],
      submittedDate: '2024-03-10',
      requiredDate: '2024-04-15',
      description: 'Replacement of aging kitchen equipment to improve efficiency',
      attachments: ['equipment_specs.pdf', 'maintenance_report.pdf'],
      workflow: [
        { step: 'Submitted', user: 'Fatima Al-Zahra', date: '2024-03-10 08:15', status: 'completed' },
        { step: 'Department Review', user: 'Operations Manager', date: '2024-03-10 16:30', status: 'completed' },
        { step: 'Budget Approval', user: 'Finance Team', date: '2024-03-12 13:45', status: 'completed' },
        { step: 'Procurement Review', user: 'Procurement Team', date: '2024-03-13 09:20', status: 'completed' },
        { step: 'Vendor Selection', user: 'Procurement Team', date: '2024-03-14 11:00', status: 'completed' },
        { step: 'PO Creation', user: 'Procurement Team', date: '2024-03-15 14:30', status: 'completed' },
        { step: 'Approved', user: 'Director', date: '2024-03-16 10:15', status: 'completed' }
      ]
    },
    {
      id: 'PR-2024-003',
      title: 'Office Furniture & Supplies',
      requester: 'Mohammed Ali',
      department: 'HR Department',
      priority: 'low',
      status: 'in-review',
      category: 'Office Supplies',
      totalAmount: 8500,
      currency: 'SAR',
      items: [
        { name: 'Office Chairs', quantity: 8, unitPrice: 800, total: 6400 },
        { name: 'Desk Organizers', quantity: 10, unitPrice: 150, total: 1500 },
        { name: 'Whiteboards', quantity: 2, unitPrice: 300, total: 600 }
      ],
      submittedDate: '2024-03-18',
      requiredDate: '2024-03-30',
      description: 'New office furniture and supplies for the HR department expansion',
      attachments: ['furniture_catalog.pdf'],
      workflow: [
        { step: 'Submitted', user: 'Mohammed Ali', date: '2024-03-18 10:45', status: 'completed' },
        { step: 'Department Review', user: 'HR Manager', date: '2024-03-18 15:20', status: 'completed' },
        { step: 'Budget Approval', user: 'Finance Team', date: '2024-03-19 12:30', status: 'current' },
        { step: 'Procurement Review', user: '', date: '', status: 'pending' },
        { step: 'Vendor Selection', user: '', date: '', status: 'pending' },
        { step: 'PO Creation', user: '', date: '', status: 'pending' },
        { step: 'Approved', user: '', date: '', status: 'pending' }
      ]
    },
    {
      id: 'PR-2024-004',
      title: 'Marketing Materials & Promotional Items',
      requester: 'Layla Ahmed',
      department: 'Marketing',
      priority: 'high',
      status: 'rejected',
      category: 'Marketing',
      totalAmount: 25000,
      currency: 'SAR',
      items: [
        { name: 'Banners & Signage', quantity: 50, unitPrice: 300, total: 15000 },
        { name: 'Promotional Items', quantity: 1000, unitPrice: 10, total: 10000 }
      ],
      submittedDate: '2024-03-12',
      requiredDate: '2024-03-20',
      description: 'Marketing materials for the upcoming product launch campaign',
      attachments: ['campaign_brief.pdf', 'design_mockups.pdf'],
      workflow: [
        { step: 'Submitted', user: 'Layla Ahmed', date: '2024-03-12 11:30', status: 'completed' },
        { step: 'Department Review', user: 'Marketing Director', date: '2024-03-12 17:45', status: 'completed' },
        { step: 'Budget Approval', user: 'Finance Team', date: '2024-03-13 14:20', status: 'rejected' },
        { step: 'Procurement Review', user: '', date: '', status: 'cancelled' },
        { step: 'Vendor Selection', user: '', date: '', status: 'cancelled' },
        { step: 'PO Creation', user: '', date: '', status: 'cancelled' },
        { step: 'Approved', user: '', date: '', status: 'cancelled' }
      ]
    },
    {
      id: 'PR-2024-005',
      title: 'Safety Equipment & PPE',
      requester: 'Omar Khalil',
      department: 'Safety & Compliance',
      priority: 'high',
      status: 'pending',
      category: 'Safety Equipment',
      totalAmount: 18000,
      currency: 'SAR',
      items: [
        { name: 'Safety Helmets', quantity: 25, unitPrice: 400, total: 10000 },
        { name: 'Safety Vests', quantity: 25, unitPrice: 200, total: 5000 },
        { name: 'Safety Gloves', quantity: 50, unitPrice: 60, total: 3000 }
      ],
      submittedDate: '2024-03-19',
      requiredDate: '2024-03-25',
      description: 'Safety equipment for warehouse and production staff',
      attachments: ['safety_standards.pdf', 'inventory_report.pdf'],
      workflow: [
        { step: 'Submitted', user: 'Omar Khalil', date: '2024-03-19 08:30', status: 'completed' },
        { step: 'Department Review', user: 'Safety Manager', date: '2024-03-19 13:15', status: 'current' },
        { step: 'Budget Approval', user: '', date: '', status: 'pending' },
        { step: 'Procurement Review', user: '', date: '', status: 'pending' },
        { step: 'Vendor Selection', user: '', date: '', status: 'pending' },
        { step: 'PO Creation', user: '', date: '', status: 'pending' },
        { step: 'Approved', user: '', date: '', status: 'pending' }
      ]
    }
  ]);

  // Create form state
  const [createForm, setCreateForm] = useState({
    title: '',
    category: '',
    priority: 'medium',
    requiredDate: '',
    description: '',
    items: [{ name: '', quantity: 1, unitPrice: 0, total: 0 }]
  });

  // Edit form state
  const [editForm, setEditForm] = useState({
    title: '',
    category: '',
    priority: 'medium',
    requiredDate: '',
    description: '',
    items: [{ name: '', quantity: 1, unitPrice: 0, total: 0 }]
  });

  // Filter requests based on search and filters
  const filteredRequests = requests.filter(request => {
    const matchesSearch = request.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         request.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         request.requester.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || request.status === statusFilter;
    const matchesPriority = priorityFilter === 'all' || request.priority === priorityFilter;
    
    return matchesSearch && matchesStatus && matchesPriority;
  });

  // Stats
  const stats = {
    total: requests.length,
    pending: requests.filter(r => r.status === 'pending').length,
    approved: requests.filter(r => r.status === 'approved').length,
    rejected: requests.filter(r => r.status === 'rejected').length,
    totalValue: requests.reduce((sum, r) => sum + r.totalAmount, 0)
  };

  // Handle form input changes
  const handleFormChange = (field, value) => {
    setCreateForm(prev => ({ ...prev, [field]: value }));
  };

  // Handle edit form input changes
  const handleEditFormChange = (field, value) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
  };

  // Handle item changes
  const handleItemChange = (index, field, value) => {
    const newItems = [...createForm.items];
    newItems[index] = { ...newItems[index], [field]: value };
    
    // Calculate total for the item
    if (field === 'quantity' || field === 'unitPrice') {
      const quantity = field === 'quantity' ? value : newItems[index].quantity;
      const unitPrice = field === 'unitPrice' ? value : newItems[index].unitPrice;
      newItems[index].total = quantity * unitPrice;
    }
    
    setCreateForm(prev => ({ ...prev, items: newItems }));
  };

  // Handle edit item changes
  const handleEditItemChange = (index, field, value) => {
    const newItems = [...editForm.items];
    newItems[index] = { ...newItems[index], [field]: value };
    
    // Calculate total for the item
    if (field === 'quantity' || field === 'unitPrice') {
      const quantity = field === 'quantity' ? value : newItems[index].quantity;
      const unitPrice = field === 'unitPrice' ? value : newItems[index].unitPrice;
      newItems[index].total = quantity * unitPrice;
    }
    
    setEditForm(prev => ({ ...prev, items: newItems }));
  };

  // Add new item
  const addItem = () => {
    setCreateForm(prev => ({
      ...prev,
      items: [...prev.items, { name: '', quantity: 1, unitPrice: 0, total: 0 }]
    }));
  };

  // Add new item to edit form
  const addEditItem = () => {
    setEditForm(prev => ({
      ...prev,
      items: [...prev.items, { name: '', quantity: 1, unitPrice: 0, total: 0 }]
    }));
  };

  // Remove item
  const removeItem = (index) => {
    if (createForm.items.length > 1) {
      setCreateForm(prev => ({
        ...prev,
        items: prev.items.filter((_, i) => i !== index)
      }));
    }
  };

  // Remove item from edit form
  const removeEditItem = (index) => {
    if (editForm.items.length > 1) {
      setEditForm(prev => ({
        ...prev,
        items: prev.items.filter((_, i) => i !== index)
      }));
    }
  };

  // View request
  const viewRequest = (request) => {
    setSelectedRequest(request);
    setShowViewModal(true);
  };

  // Edit request
  const editRequest = (request) => {
    setSelectedRequest(request);
    setEditForm({
      title: request.title,
      category: request.category,
      priority: request.priority,
      requiredDate: request.requiredDate,
      description: request.description,
      items: request.items.map(item => ({ ...item }))
    });
    setShowEditModal(true);
  };

  // Save edited request
  const saveEditRequest = () => {
    setRequests(prev => prev.map(request => 
      request.id === selectedRequest.id 
        ? {
            ...request,
            title: editForm.title,
            category: editForm.category,
            priority: editForm.priority,
            requiredDate: editForm.requiredDate,
            description: editForm.description,
            items: editForm.items.filter(item => item.name.trim() !== ''),
            totalAmount: editForm.items.reduce((sum, item) => sum + (item.total || 0), 0)
          }
        : request
    ));
    setShowEditModal(false);
    setSelectedRequest(null);
  };

  // Create new request
  const createRequest = () => {
    const newRequest = {
      id: `PR-2024-${String(requests.length + 1).padStart(3, '0')}`,
      title: createForm.title,
      requester: 'Current User',
      department: 'Current Department',
      priority: createForm.priority,
      status: 'pending',
      category: createForm.category,
      totalAmount: createForm.items.reduce((sum, item) => sum + (item.total || 0), 0),
      currency: 'SAR',
      items: createForm.items.filter(item => item.name.trim() !== ''),
      submittedDate: new Date().toISOString().split('T')[0],
      requiredDate: createForm.requiredDate,
      description: createForm.description,
      attachments: [],
      workflow: [
        { step: 'Submitted', user: 'Current User', date: new Date().toLocaleString(), status: 'completed' },
        { step: 'Department Review', user: '', date: '', status: 'pending' },
        { step: 'Budget Approval', user: '', date: '', status: 'pending' },
        { step: 'Procurement Review', user: '', date: '', status: 'pending' },
        { step: 'Vendor Selection', user: '', date: '', status: 'pending' },
        { step: 'PO Creation', user: '', date: '', status: 'pending' },
        { step: 'Approved', user: '', date: '', status: 'pending' }
      ]
    };

    setRequests(prev => [newRequest, ...prev]);
    setShowCreateModal(false);
    setCreateForm({
      title: '',
      category: '',
      priority: 'medium',
      requiredDate: '',
      description: '',
      items: [{ name: '', quantity: 1, unitPrice: 0, total: 0 }]
    });
  };

  // Delete request
  const deleteRequest = (id) => {
    setRequests(prev => prev.filter(request => request.id !== id));
  };

  // Show confirmation modal
  const showConfirmation = (type, requestId, requestTitle) => {
    setConfirmAction({ type, requestId, requestTitle });
    setShowConfirmModal(true);
  };

  // Handle confirmation
  const handleConfirmation = () => {
    if (confirmAction.type === 'approve') {
      approveRequest(confirmAction.requestId);
    } else if (confirmAction.type === 'reject') {
      rejectRequest(confirmAction.requestId);
    } else if (confirmAction.type === 'delete') {
      deleteRequest(confirmAction.requestId);
    }
    setShowConfirmModal(false);
  };

  // Approve request
  const approveRequest = (id) => {
    setRequests(prev => prev.map(request => 
      request.id === id 
        ? { ...request, status: 'approved' }
        : request
    ));
  };

  // Reject request
  const rejectRequest = (id) => {
    setRequests(prev => prev.map(request => 
      request.id === id 
        ? { ...request, status: 'rejected' }
        : request
    ));
  };

  // Export data
  const exportData = () => {
    const csvContent = [
      ['ID', 'Title', 'Requester', 'Department', 'Priority', 'Status', 'Category', 'Total Amount', 'Submitted Date', 'Required Date'],
      ...filteredRequests.map(request => [
        request.id,
        request.title,
        request.requester,
        request.department,
        request.priority,
        request.status,
        request.category,
        `${request.currency} ${request.totalAmount.toLocaleString()}`,
        request.submittedDate,
        request.requiredDate
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `purchase-requests-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getPriorityColor = (priority) => {
    const colors = {
      high: 'bg-red-600 text-white',
      medium: 'bg-amber-600 text-white',
      low: 'bg-blue-600 text-white'
    };
    return colors[priority] || colors.medium;
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-amber-600 text-white',
      'in-review': 'bg-blue-600 text-white',
      approved: 'bg-green-600 text-white',
      rejected: 'bg-red-600 text-white'
    };
    return colors[status] || colors.pending;
  };

  const getWorkflowStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle2 className="w-4 h-4 text-green-600" />;
      case 'current': return <Clock className="w-4 h-4 text-blue-600" />;
      case 'rejected': return <XCircle className="w-4 h-4 text-red-600" />;
      case 'cancelled': return <XCircle className="w-4 h-4 text-gray-400" />;
      default: return <Clock className="w-4 h-4 text-gray-400" />;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const navigate = useNavigate();

  return (
    <div className="space-y-6">
      {/* Header with Stats */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Purchase Requests</h2>
            <p className="text-sm text-gray-500">Manage and track internal purchase requests</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            New Request
          </button>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[
            { label: 'Total Requests', value: stats.total, icon: FileText, color: 'blue' },
            { label: 'Pending', value: stats.pending, icon: Clock, color: 'amber' },
            { label: 'Approved', value: stats.approved, icon: CheckCircle2, color: 'green' },
            { label: 'Rejected', value: stats.rejected, icon: XCircle, color: 'red' },
            { label: 'Total Value', value: `SAR ${(stats.totalValue / 1000).toFixed(0)}K`, icon: DollarSign, color: 'emerald' }
          ].map((stat, index) => (
            <motion.div
              key={index}
              whileHover={{ scale: 1.02 }}
              className="flex items-center p-4 bg-gray-50 rounded-lg"
            >
              <div className={`p-2 rounded-lg bg-${stat.color}-600 text-white mr-3`}>
                <stat.icon className="w-5 h-5" />
              </div>
              <div>
                <p className="text-sm text-gray-500">{stat.label}</p>
                <p className="text-lg font-semibold text-gray-900">{stat.value}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search requests, requester, or ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
            </div>
          </div>
          <div className="flex gap-3">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="in-review">In Review</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
            <select
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            >
              <option value="all">All Priority</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
            <button className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors" onClick={exportData}>
              <Download className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Requests Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">
                  Request Details
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">
                  Requester
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">
                  Amount
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                  Priority
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">
                  Dates
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredRequests.map((request) => (
                <React.Fragment key={request.id}>
                  <tr className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-8 w-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                          <Package className="w-4 h-4 text-emerald-600" />
                        </div>
                        <div className="ml-3 min-w-0 flex-1">
                          <div className="text-sm font-medium text-gray-900 truncate">{request.title}</div>
                          <div className="text-xs text-gray-500">{request.id}</div>
                          <div className="text-xs text-gray-400 truncate">{request.category}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-6 w-6 bg-blue-100 rounded-full flex items-center justify-center">
                          <User className="w-3 h-3 text-blue-600" />
                        </div>
                        <div className="ml-2 min-w-0 flex-1">
                          <div className="text-sm font-medium text-gray-900 truncate">{request.requester}</div>
                          <div className="text-xs text-gray-500 truncate">{request.department}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium text-gray-900">
                        {request.currency} {request.totalAmount.toLocaleString()}
                      </div>
                      <div className="text-xs text-gray-500">{request.items.length} items</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${getPriorityColor(request.priority)}`}>
                        {request.priority.charAt(0).toUpperCase() + request.priority.slice(1)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${getStatusColor(request.status)}`}>
                        {request.status === 'in-review' ? 'In Review' : 
                         request.status.split('-').map(word => 
                           word.charAt(0).toUpperCase() + word.slice(1)
                         ).join(' ')}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-xs text-gray-900">
                        <div className="truncate">Submitted: {formatDate(request.submittedDate)}</div>
                        <div className="text-gray-500 truncate">Required: {formatDate(request.requiredDate)}</div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center space-x-1">
                        <button
                          onClick={() => setExpandedRequest(expandedRequest === request.id ? null : request.id)}
                          className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                          title="Toggle Details"
                        >
                          {expandedRequest === request.id ? (
                            <ChevronUp className="w-3.5 h-3.5" />
                          ) : (
                            <ChevronDown className="w-3.5 h-3.5" />
                          )}
                        </button>
                        <button 
                          onClick={() => viewRequest(request)}
                          className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                          title="View Details"
                        >
                          <Eye className="w-3.5 h-3.5" />
                        </button>
                        <button 
                          onClick={() => editRequest(request)}
                          className="p-1.5 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded transition-colors"
                          title="Edit Request"
                        >
                          <Edit className="w-3.5 h-3.5" />
                        </button>
                        {request.status === 'pending' && (
                          <>
                            <button 
                              onClick={() => showConfirmation('approve', request.id, request.title)}
                              className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded transition-colors"
                              title="Approve Request"
                            >
                              <CheckCircle2 className="w-3.5 h-3.5" />
                            </button>
                            <button 
                              onClick={() => showConfirmation('reject', request.id, request.title)}
                              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                              title="Reject Request"
                            >
                              <XCircle className="w-3.5 h-3.5" />
                            </button>
                          </>
                        )}
                        <button 
                          onClick={() => showConfirmation('delete', request.id, request.title)}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                          title="Delete Request"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                  
                  {/* Expanded Workflow Section */}
                  <AnimatePresence>
                    {expandedRequest === request.id && (
                      <motion.tr
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        <td colSpan="7" className="px-4 py-4 bg-gray-50">
                          <div className="space-y-4">
                            {/* Workflow Timeline */}
                            <div>
                              <h4 className="text-sm font-medium text-gray-900 mb-3">Approval Workflow</h4>
                              <div className="relative">
                                <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-200"></div>
                                <div className="space-y-3">
                                  {request.workflow.map((step, index) => (
                                    <div key={index} className="relative flex items-center">
                                      <div className={`relative z-10 flex items-center justify-center w-12 h-12 rounded-full border-2 ${
                                        step.status === 'completed' ? 'bg-green-600 border-green-600' :
                                        step.status === 'current' ? 'bg-blue-600 border-blue-600' :
                                        step.status === 'rejected' ? 'bg-red-600 border-red-600' :
                                        'bg-white border-gray-300'
                                      }`}>
                                        {getWorkflowStatusIcon(step.status)}
                                      </div>
                                      <div className="ml-3 flex-1">
                                        <div className="flex items-center justify-between">
                                          <h5 className="text-sm font-medium text-gray-900">{step.step}</h5>
                                          {step.date && (
                                            <span className="text-xs text-gray-500">{step.date}</span>
                                          )}
                                        </div>
                                        {step.user && (
                                          <p className="text-xs text-gray-500">by {step.user}</p>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>

                            {/* Items List */}
                            <div>
                              <h4 className="text-sm font-medium text-gray-900 mb-2">Requested Items</h4>
                              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                                <table className="w-full">
                                  <thead className="bg-gray-50">
                                    <tr>
                                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Item</th>
                                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Qty</th>
                                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Unit Price</th>
                                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Total</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-gray-200">
                                    {request.items.map((item, index) => (
                                      <tr key={index}>
                                        <td className="px-3 py-2 text-sm text-gray-900">{item.name}</td>
                                        <td className="px-3 py-2 text-sm text-gray-500">{item.quantity}</td>
                                        <td className="px-3 py-2 text-sm text-gray-500">{request.currency} {item.unitPrice.toLocaleString()}</td>
                                        <td className="px-3 py-2 text-sm font-medium text-gray-900">{request.currency} {item.total.toLocaleString()}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>

                            {/* Description and Attachments */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <div>
                                <h4 className="text-sm font-medium text-gray-900 mb-2">Description</h4>
                                <p className="text-sm text-gray-600 bg-white p-3 rounded-lg border border-gray-200">
                                  {request.description}
                                </p>
                              </div>
                              <div>
                                <h4 className="text-sm font-medium text-gray-900 mb-2">Attachments</h4>
                                <div className="space-y-1">
                                  {request.attachments.map((attachment, index) => (
                                    <div key={index} className="flex items-center p-2 bg-white border border-gray-200 rounded-lg">
                                      <FileText className="w-3 h-3 text-gray-400 mr-2" />
                                      <span className="text-xs text-gray-600">{attachment}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>
                        </td>
                      </motion.tr>
                    )}
                  </AnimatePresence>
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create Request Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowCreateModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Create New Purchase Request</h3>
                  <button
                    onClick={() => setShowCreateModal(false)}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <XCircle className="w-5 h-5" />
                  </button>
                </div>
              </div>
              
              <div className="p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Request Title</label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      placeholder="Enter request title"
                      value={createForm.title}
                      onChange={(e) => handleFormChange('title', e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                    <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      value={createForm.category}
                      onChange={(e) => handleFormChange('category', e.target.value)}
                    >
                      <option>Technology</option>
                      <option>Equipment</option>
                      <option>Office Supplies</option>
                      <option>Marketing</option>
                      <option>Safety Equipment</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
                    <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      value={createForm.priority}
                      onChange={(e) => handleFormChange('priority', e.target.value)}
                    >
                      <option>Low</option>
                      <option>Medium</option>
                      <option>High</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Required Date</label>
                    <input
                      type="date"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      value={createForm.requiredDate}
                      onChange={(e) => handleFormChange('requiredDate', e.target.value)}
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="Describe the items needed and reason for request"
                    value={createForm.description}
                    onChange={(e) => handleFormChange('description', e.target.value)}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Items</label>
                  <div className="space-y-3">
                    <div className="grid grid-cols-4 gap-3">
                      <input
                        type="text"
                        placeholder="Item name"
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                        value={createForm.items[0].name}
                        onChange={(e) => handleItemChange(0, 'name', e.target.value)}
                      />
                      <input
                        type="number"
                        placeholder="Qty"
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                        value={createForm.items[0].quantity}
                        onChange={(e) => handleItemChange(0, 'quantity', parseInt(e.target.value) || 0)}
                      />
                      <input
                        type="number"
                        placeholder="Unit price"
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                        value={createForm.items[0].unitPrice}
                        onChange={(e) => handleItemChange(0, 'unitPrice', parseInt(e.target.value) || 0)}
                      />
                      <button className="px-3 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
                        onClick={addItem}
                      >
                        Add Item
                      </button>
                    </div>
                    {createForm.items.slice(1).map((item, index) => (
                      <div key={index} className="grid grid-cols-4 gap-3">
                        <input
                          type="text"
                          placeholder="Item name"
                          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                          value={item.name}
                          onChange={(e) => handleItemChange(index + 1, 'name', e.target.value)}
                        />
                        <input
                          type="number"
                          placeholder="Qty"
                          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                          value={item.quantity}
                          onChange={(e) => handleItemChange(index + 1, 'quantity', parseInt(e.target.value) || 0)}
                        />
                        <input
                          type="number"
                          placeholder="Unit price"
                          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                          value={item.unitPrice}
                          onChange={(e) => handleItemChange(index + 1, 'unitPrice', parseInt(e.target.value) || 0)}
                        />
                        <button className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                          onClick={() => removeItem(index + 1)}
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
                  onClick={createRequest}
                >
                  Create Request
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* View Request Modal */}
      <AnimatePresence>
        {showViewModal && selectedRequest && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowViewModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden border border-gray-100 print:max-w-full print:rounded-none print:shadow-none print:border-0 relative"
              onClick={e => e.stopPropagation()}
            >
              {/* Header */}
              <div className="relative bg-gradient-to-r from-emerald-600 to-teal-600 text-white p-8 print:bg-white print:text-gray-900">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-6">
                    <div className="p-4 bg-white/20 backdrop-blur-sm rounded-2xl">
                      <FileText className="h-8 w-8 text-white" />
                    </div>
                    <div>
                      <h2 className="text-3xl font-bold tracking-tight">Purchase Request</h2>
                      <p className="text-emerald-100 text-lg mt-1 print:text-gray-600">Official Request Document</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowViewModal(false)}
                    className="p-3 hover:bg-white/10 rounded-xl transition-colors print:hidden"
                  >
                    <XCircle className="h-6 w-6 text-white" />
                  </button>
                </div>
              </div>

              {/* Document Content */}
              <div className="overflow-y-auto max-h-[calc(90vh-200px)] print:max-h-none">
                {/* Document Header */}
                <div className="px-8 pt-8 pb-6 border-b-2 border-gray-100 print:px-6 print:pt-6">
                  <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-8">
                    <div className="flex-1">
                      <div className="flex items-center gap-6 mb-4">
                        <div className="w-20 h-20 bg-gradient-to-br from-emerald-600 to-teal-600 rounded-2xl flex items-center justify-center shadow-lg">
                          <span className="text-white font-bold text-2xl">PR</span>
                        </div>
                        <div>
                          <h1 className="text-4xl font-black text-gray-900 tracking-tight leading-tight">{selectedRequest.title}</h1>
                          <p className="text-xl text-gray-600 font-medium mt-2">Request ID: {selectedRequest.id}</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4 text-gray-400" />
                          <span className="text-gray-600 font-medium">Department:</span>
                          <span className="text-gray-900">{selectedRequest.department}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Package className="h-4 w-4 text-gray-400" />
                          <span className="text-gray-600 font-medium">Category:</span>
                          <span className="text-gray-900">{selectedRequest.category}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-gray-400" />
                          <span className="text-gray-600 font-medium">Date:</span>
                          <span className="text-gray-900">{formatDate(selectedRequest.submittedDate)}</span>
                        </div>
                      </div>
                    </div>
                    {/* QR Code */}
                    <div className="flex flex-col items-center gap-3 bg-gray-50 rounded-2xl p-6 print:bg-white">
                      <div className="w-28 h-28 bg-white rounded-xl flex items-center justify-center shadow-sm border border-gray-200">
                        <QRCodeSVG 
                          value={`Request:${selectedRequest.id}|${selectedRequest.title}`} 
                          size={100} 
                          level="M"
                          className="rounded-lg"
                        />
                      </div>
                      <div className="text-center">
                        <p className="text-xs font-medium text-gray-600">Scan for details</p>
                        <p className="text-xs text-gray-400">Doc ID: {selectedRequest.id}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Status Cards */}
                <div className="px-8 pt-8 print:px-6 print:pt-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-2xl border border-blue-200">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="p-2 bg-blue-600 rounded-lg">
                          <Activity className="h-5 w-5 text-white" />
                        </div>
                        <h3 className="text-lg font-semibold text-blue-900">Status</h3>
                      </div>
                      <span className={`inline-flex px-4 py-2 rounded-full text-sm font-semibold ${getStatusColor(selectedRequest.status)}`}>
                        {selectedRequest.status === 'in-review' ? 'In Review' : selectedRequest.status.charAt(0).toUpperCase() + selectedRequest.status.slice(1)}
                      </span>
                    </div>
                    <div className="bg-gradient-to-br from-amber-50 to-amber-100 p-6 rounded-2xl border border-amber-200">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="p-2 bg-amber-600 rounded-lg">
                          <AlertTriangle className="h-5 w-5 text-white" />
                        </div>
                        <h3 className="text-lg font-semibold text-amber-900">Priority</h3>
                      </div>
                      <span className={`inline-flex px-4 py-2 rounded-full text-sm font-semibold ${getPriorityColor(selectedRequest.priority)}`}>
                        {selectedRequest.priority.charAt(0).toUpperCase() + selectedRequest.priority.slice(1)}
                      </span>
                    </div>
                    <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 p-6 rounded-2xl border border-emerald-200">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="p-2 bg-emerald-600 rounded-lg">
                          <DollarSign className="h-5 w-5 text-white" />
                        </div>
                        <h3 className="text-lg font-semibold text-emerald-900">Total Value</h3>
                      </div>
                      <p className="text-3xl font-bold text-emerald-900">{selectedRequest.currency} {selectedRequest.totalAmount.toLocaleString()}</p>
                    </div>
                  </div>
                </div>

                {/* Main Content */}
                <div className="px-8 pb-32 print:px-6 print:pb-6">
                  {/* Description */}
                  <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-6 border border-gray-200 mb-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-3">
                      <FileText className="h-5 w-5 text-emerald-600" />
                      Description
                    </h3>
                    <p className="text-gray-700 leading-relaxed">{selectedRequest.description}</p>
                  </div>

                  {/* Compact Info Cards - Two Rows */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                    {/* Requester Info */}
                    <div className="bg-white border-2 border-gray-200 rounded-xl p-4 shadow-sm">
                      <div className="flex items-center gap-2 mb-2">
                        <User className="h-4 w-4 text-emerald-600" />
                        <h4 className="text-sm font-bold text-gray-900">Requester</h4>
                      </div>
                      <p className="text-sm text-gray-700">{selectedRequest.requester}</p>
                    </div>

                    {/* Department */}
                    <div className="bg-white border-2 border-gray-200 rounded-xl p-4 shadow-sm">
                      <div className="flex items-center gap-2 mb-2">
                        <Building2 className="h-4 w-4 text-emerald-600" />
                        <h4 className="text-sm font-bold text-gray-900">Department</h4>
                      </div>
                      <p className="text-sm text-gray-700">{selectedRequest.department}</p>
                    </div>

                    {/* Category */}
                    <div className="bg-white border-2 border-gray-200 rounded-xl p-4 shadow-sm">
                      <div className="flex items-center gap-2 mb-2">
                        <Package className="h-4 w-4 text-emerald-600" />
                        <h4 className="text-sm font-bold text-gray-900">Category</h4>
                      </div>
                      <p className="text-sm text-gray-700">{selectedRequest.category}</p>
                    </div>

                    {/* Submitted Date */}
                    <div className="bg-white border-2 border-gray-200 rounded-xl p-4 shadow-sm">
                      <div className="flex items-center gap-2 mb-2">
                        <Calendar className="h-4 w-4 text-emerald-600" />
                        <h4 className="text-sm font-bold text-gray-900">Submitted</h4>
                      </div>
                      <p className="text-sm text-gray-700">{formatDate(selectedRequest.submittedDate)}</p>
                    </div>
                  </div>

                  {/* Workflow Status - Compact Horizontal */}
                  <div className="bg-white border-2 border-gray-200 rounded-2xl p-6 shadow-sm mb-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-3">
                      <Workflow className="h-5 w-5 text-emerald-600" />
                      Workflow Status
                    </h3>
                    <div className="flex flex-wrap gap-3">
                      {selectedRequest.workflow.map((step, index) => (
                        <div key={index} className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2 border border-gray-200">
                          <div className={`w-6 h-6 rounded-lg flex items-center justify-center ${
                            step.status === 'completed' ? 'bg-emerald-100 border border-emerald-200' : 
                            step.status === 'current' ? 'bg-blue-100 border border-blue-200' : 'bg-gray-100 border border-gray-200'
                          }`}>
                            {getWorkflowStatusIcon(step.status)}
                          </div>
                          <div>
                            <p className={`text-xs font-semibold ${
                              step.status === 'completed' ? 'text-emerald-900' :
                              step.status === 'current' ? 'text-blue-900' : 'text-gray-500'
                            }`}>
                              {step.step}
                            </p>
                            {step.user && (
                              <p className="text-xs text-gray-500">{step.user}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Items Table - Full Width */}
                  <div className="bg-white border-2 border-gray-200 rounded-2xl overflow-hidden shadow-sm">
                    <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b-2 border-gray-200">
                      <h3 className="text-lg font-bold text-gray-900 flex items-center gap-3">
                        <Package className="h-5 w-5 text-emerald-600" />
                        Requested Items
                      </h3>
                    </div>
                    <div className="p-6">
                      <div className="space-y-4">
                        {selectedRequest.items.map((item, index) => (
                          <div key={index} className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-center">
                              <div className="md:col-span-2">
                                <h4 className="text-lg font-semibold text-gray-900 mb-1">{item.name}</h4>
                              </div>
                              <div className="text-center">
                                <span className="text-sm text-gray-500 font-medium block mb-1">Quantity</span>
                                <p className="text-lg font-bold text-gray-900">{item.quantity}</p>
                              </div>
                              <div className="text-center">
                                <span className="text-sm text-gray-500 font-medium block mb-1">Unit Price</span>
                                <p className="text-lg font-bold text-gray-900">{selectedRequest.currency} {item.unitPrice.toLocaleString()}</p>
                              </div>
                              <div className="text-center">
                                <span className="text-sm text-gray-500 font-medium block mb-1">Total</span>
                                <p className="text-xl font-black text-emerald-900">{selectedRequest.currency} {item.total.toLocaleString()}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      {/* Total Summary */}
                      <div className="mt-6 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-5 border-2 border-emerald-200">
                        <div className="flex items-center justify-between">
                          <span className="text-xl font-bold text-gray-900">Total Amount:</span>
                          <span className="text-3xl font-black text-emerald-900">{selectedRequest.currency} {selectedRequest.totalAmount.toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating Action Buttons */}
              <div className="fixed bottom-8 left-1/2 transform -translate-x-1/2 flex items-center gap-4 bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl border border-gray-200/50 px-8 py-4 print:hidden z-10">
                <button
                  onClick={() => {
                    setShowViewModal(false);
                    navigate(`/supply-chain/purchase-requests/${selectedRequest.id}`);
                  }}
                  className="px-8 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-2xl hover:from-emerald-700 hover:to-teal-700 transition-all duration-300 flex items-center gap-3 font-bold text-base shadow-lg hover:shadow-xl transform hover:scale-105"
                >
                  <FileText className="h-5 w-5" />
                  <span>View Full Details</span>
                </button>
                <button
                  onClick={() => window.print()}
                  className="px-6 py-3 border-2 border-gray-300 rounded-2xl text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-all duration-300 flex items-center gap-3 font-bold text-base shadow-lg hover:shadow-xl transform hover:scale-105"
                >
                  <Download className="h-5 w-5" />
                  <span>Print / PDF</span>
                </button>
                <button
                  onClick={() => setShowViewModal(false)}
                  className="px-6 py-3 border-2 border-gray-300 rounded-2xl text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-all duration-300 font-bold text-base shadow-lg hover:shadow-xl transform hover:scale-105"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit Request Modal */}
      <AnimatePresence>
        {showEditModal && selectedRequest && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowEditModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Edit Purchase Request</h3>
                  <button
                    onClick={() => setShowEditModal(false)}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <XCircle className="w-5 h-5" />
                  </button>
                </div>
              </div>
              
              <div className="p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Request Title</label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      placeholder="Enter request title"
                      value={editForm.title}
                      onChange={(e) => handleEditFormChange('title', e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                    <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      value={editForm.category}
                      onChange={(e) => handleEditFormChange('category', e.target.value)}
                    >
                      <option>Technology</option>
                      <option>Equipment</option>
                      <option>Office Supplies</option>
                      <option>Marketing</option>
                      <option>Safety Equipment</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
                    <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      value={editForm.priority}
                      onChange={(e) => handleEditFormChange('priority', e.target.value)}
                    >
                      <option>Low</option>
                      <option>Medium</option>
                      <option>High</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Required Date</label>
                    <input
                      type="date"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      value={editForm.requiredDate}
                      onChange={(e) => handleEditFormChange('requiredDate', e.target.value)}
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="Describe the items needed and reason for request"
                    value={editForm.description}
                    onChange={(e) => handleEditFormChange('description', e.target.value)}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Items</label>
                  <div className="space-y-3">
                    <div className="grid grid-cols-4 gap-3">
                      <input
                        type="text"
                        placeholder="Item name"
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                        value={editForm.items[0].name}
                        onChange={(e) => handleEditItemChange(0, 'name', e.target.value)}
                      />
                      <input
                        type="number"
                        placeholder="Qty"
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                        value={editForm.items[0].quantity}
                        onChange={(e) => handleEditItemChange(0, 'quantity', parseInt(e.target.value) || 0)}
                      />
                      <input
                        type="number"
                        placeholder="Unit price"
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                        value={editForm.items[0].unitPrice}
                        onChange={(e) => handleEditItemChange(0, 'unitPrice', parseInt(e.target.value) || 0)}
                      />
                      <button className="px-3 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
                        onClick={addEditItem}
                      >
                        Add Item
                      </button>
                    </div>
                    {editForm.items.slice(1).map((item, index) => (
                      <div key={index} className="grid grid-cols-4 gap-3">
                        <input
                          type="text"
                          placeholder="Item name"
                          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                          value={item.name}
                          onChange={(e) => handleEditItemChange(index + 1, 'name', e.target.value)}
                        />
                        <input
                          type="number"
                          placeholder="Qty"
                          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                          value={item.quantity}
                          onChange={(e) => handleEditItemChange(index + 1, 'quantity', parseInt(e.target.value) || 0)}
                        />
                        <input
                          type="number"
                          placeholder="Unit price"
                          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                          value={item.unitPrice}
                          onChange={(e) => handleEditItemChange(index + 1, 'unitPrice', parseInt(e.target.value) || 0)}
                        />
                        <button className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                          onClick={() => removeEditItem(index + 1)}
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
                <button
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
                  onClick={saveEditRequest}
                >
                  Save Changes
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Confirmation Modal */}
      <AnimatePresence>
        {showConfirmModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowConfirmModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl shadow-xl max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center mb-4">
                  <div className={`p-2 rounded-lg mr-3 ${
                    confirmAction.type === 'approve' ? 'bg-green-100' :
                    confirmAction.type === 'reject' ? 'bg-red-100' :
                    'bg-gray-100'
                  }`}>
                    {confirmAction.type === 'approve' ? (
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                    ) : confirmAction.type === 'reject' ? (
                      <XCircle className="w-5 h-5 text-red-600" />
                    ) : (
                      <Trash2 className="w-5 h-5 text-gray-600" />
                    )}
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {confirmAction.type === 'approve' ? 'Approve Request' :
                     confirmAction.type === 'reject' ? 'Reject Request' :
                     'Delete Request'}
                  </h3>
                </div>
                <p className="text-sm text-gray-600">
                  Are you sure you want to {confirmAction.type} the request titled 
                  <span className="font-medium text-gray-900"> "{confirmAction.requestTitle}"</span>?
                </p>
                {confirmAction.type === 'delete' && (
                  <p className="text-xs text-red-600 mt-2">
                    This action cannot be undone.
                  </p>
                )}
              </div>
              <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
                <button
                  onClick={() => setShowConfirmModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmation}
                  className={`px-4 py-2 text-white rounded-lg transition-colors ${
                    confirmAction.type === 'approve' ? 'bg-green-600 hover:bg-green-700' :
                    confirmAction.type === 'reject' ? 'bg-red-600 hover:bg-red-700' :
                    'bg-red-600 hover:bg-red-700'
                  }`}
                >
                  {confirmAction.type === 'approve' ? 'Approve' :
                   confirmAction.type === 'reject' ? 'Reject' :
                   'Delete'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PurchaseRequestsTab; 